import {useCallback, useEffect, useMemo, useState} from "react";

export type DismissibleStorageStrategy =
    | { type: "forever" }
    | { type: "daily", resetHour: number };

const STORAGE_PAYLOAD_VERSION = 1;

interface DismissibleStoragePayload {
    v: number;
    dismissedAt: number;
}

interface UseDismissibleStorageParams {
    storageKey: string;
    strategy?: DismissibleStorageStrategy;
}

const DEFAULT_STRATEGY: DismissibleStorageStrategy = {type: "forever"};

const isBrowser = () => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const normalizeResetHour = (resetHour: number) => {
    if (!Number.isFinite(resetHour)) {
        return 0;
    }

    return Math.min(23, Math.max(0, Math.floor(resetHour)));
};

const getPeriodStart = (nowMs: number, resetHour: number) => {
    const periodStart = new Date(nowMs);
    periodStart.setHours(normalizeResetHour(resetHour), 0, 0, 0);

    if (periodStart.getTime() > nowMs) {
        periodStart.setDate(periodStart.getDate() - 1);
    }

    return periodStart.getTime();
};

const getNextPeriodStart = (nowMs: number, resetHour: number) => {
    const periodStart = new Date(getPeriodStart(nowMs, resetHour));
    periodStart.setDate(periodStart.getDate() + 1);
    return periodStart.getTime();
};

const parseDismissedAt = (raw: string | null): number | "legacyHidden" | null => {
    if (raw === null) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as unknown;

        if (parsed === true) {
            return "legacyHidden";
        }

        if (typeof parsed === "number" && Number.isFinite(parsed)) {
            return parsed;
        }

        if (
            parsed &&
            typeof parsed === "object" &&
            "dismissedAt" in parsed &&
            typeof (parsed as DismissibleStoragePayload).dismissedAt === "number" &&
            Number.isFinite((parsed as DismissibleStoragePayload).dismissedAt)
        ) {
            return (parsed as DismissibleStoragePayload).dismissedAt;
        }
    } catch {
        return null;
    }

    return null;
};

const isHiddenByStrategy = (dismissedAt: number | "legacyHidden" | null, strategy: DismissibleStorageStrategy, nowMs: number) => {
    if (dismissedAt === null) {
        return false;
    }

    if (dismissedAt === "legacyHidden") {
        return true;
    }

    if (strategy.type === "forever") {
        return true;
    }

    return dismissedAt >= getPeriodStart(nowMs, strategy.resetHour);
};

const readIsHidden = (storageKey: string, strategy: DismissibleStorageStrategy) => {
    if (!isBrowser()) {
        return false;
    }

    const raw = window.localStorage.getItem(storageKey);
    const dismissedAt = parseDismissedAt(raw);

    if (dismissedAt === "legacyHidden" && strategy.type === "daily") {
        const migratedPayload = createDismissPayload(Date.now());
        window.localStorage.setItem(storageKey, JSON.stringify(migratedPayload));
        return isHiddenByStrategy(migratedPayload.dismissedAt, strategy, Date.now());
    }

    return isHiddenByStrategy(dismissedAt, strategy, Date.now());
};

const createDismissPayload = (nowMs: number): DismissibleStoragePayload => ({
    v: STORAGE_PAYLOAD_VERSION,
    dismissedAt: nowMs,
});

export const dismissibleStorageStrategies = {
    forever: (): DismissibleStorageStrategy => ({type: "forever"}),
    dailyAtHour: (resetHour: number): DismissibleStorageStrategy => ({type: "daily", resetHour: normalizeResetHour(resetHour)}),
};

export const useDismissibleStorage = ({storageKey, strategy = DEFAULT_STRATEGY}: UseDismissibleStorageParams) => {
    const normalizedStrategy = useMemo<DismissibleStorageStrategy>(() => {
        if (strategy.type === "daily") {
            return {type: "daily", resetHour: normalizeResetHour(strategy.resetHour)};
        }

        return DEFAULT_STRATEGY;
    }, [strategy.type, strategy.type === "daily" ? strategy.resetHour : undefined]);

    const [isHidden, setIsHidden] = useState(() => readIsHidden(storageKey, normalizedStrategy));

    const hide = useCallback(() => {
        if (isBrowser()) {
            window.localStorage.setItem(storageKey, JSON.stringify(createDismissPayload(Date.now())));
        }

        setIsHidden(true);
    }, [storageKey]);

    const show = useCallback(() => {
        if (isBrowser()) {
            window.localStorage.removeItem(storageKey);
        }

        setIsHidden(false);
    }, [storageKey]);

    useEffect(() => {
        setIsHidden(readIsHidden(storageKey, normalizedStrategy));
    }, [storageKey, normalizedStrategy]);

    const timerDelay = useMemo(() => {
        if (!isHidden || normalizedStrategy.type !== "daily") {
            return null;
        }

        const nowMs = Date.now();
        return Math.max(1, getNextPeriodStart(nowMs, normalizedStrategy.resetHour) - nowMs);
    }, [isHidden, normalizedStrategy]);

    useEffect(() => {
        if (timerDelay === null) {
            return;
        }

        const timerId = window.setTimeout(() => {
            setIsHidden(readIsHidden(storageKey, normalizedStrategy));
        }, timerDelay);

        return () => window.clearTimeout(timerId);
    }, [storageKey, normalizedStrategy, timerDelay]);

    return {isHidden, hide, show};
};
